import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect, useRef } from "react";

import { ICoordinates, ISandboxMarker } from "../components/Map";
import { getAllInterestPoints } from "src/services/interestPoints";
import { InterestPoint } from "src/types/interestPoints";

interface MapDrawingContextProps {
  handleMapClick?: (coords: ICoordinates) => void;
  circleDrawing?: { center: ICoordinates; radius: number; type: String };
  polygonDrawing?: {
    points: ICoordinates[];
    type: "interest" | "avoid";
  };
  setCircleDrawing: React.Dispatch<
    React.SetStateAction<
      { center: ICoordinates; radius: number; type: "interest" | "avoid"; new?: boolean } | undefined
    >
  >;
  setPolygonDrawing: React.Dispatch<
    React.SetStateAction<{ points: ICoordinates[]; type: "interest" | "avoid"; new?: boolean } | undefined>
  >;
  handleChangeRadius: (newRadius: number) => void;
  radius: number;
  radiusInput: string;
  setRadiusInput: React.Dispatch<React.SetStateAction<string>>;
  drawingType: "CIRCLE" | "POLYGON" | null;
  setDrawingType: (type: "CIRCLE" | "POLYGON" | null) => void;
  drawingPoints: ICoordinates[];
  setDrawingPoints: React.Dispatch<React.SetStateAction<ICoordinates[]>>;
  type: string;
  setType: (type: string) => void;
  formattedAddress: string;
  setFormattedAddress: (value: string) => void;
  shape: string;
  setShape: (value: string) => void;
  selectedOption: number;
  setSelectedOption: (value: number) => void;
  drawingMarkers: ISandboxMarker[];
  setDrawingMarkers: React.Dispatch<React.SetStateAction<ISandboxMarker[]>>;
  enableDrawingClick: boolean;
  setEnableDrawingClick: (value: boolean) => void;
  enableDrawingRef: React.MutableRefObject<boolean>;
  enableContextMenuClick: boolean;
  setEnableContextMenuClick: (value: boolean) => void;
  enableContextMenuRef: React.MutableRefObject<boolean>;
  mapZoom: number;
  setMapZoom: React.Dispatch<React.SetStateAction<number>>;
  setCenterMarker: React.Dispatch<React.SetStateAction<ICoordinates | null>>;
  centerMarker?: ICoordinates | null;
  sortPointsClockwise: (points: ICoordinates[]) => ICoordinates[];
  mapCenter: { lat: number; lng: number };
  setMapCenter: React.Dispatch<React.SetStateAction<{ lat: number; lng: number }>>;
  interestPoints: InterestPoint[];
}

const MapDrawingContext = createContext<MapDrawingContextProps | undefined>(undefined);

export const useMapDrawingContext = () => {
  const context = useContext(MapDrawingContext);
  if (!context) throw new Error("useMapDrawingContext must be used within a MapDrawingProvider");
  return context;
};

export const MapDrawingProvider = ({ children }: { children: ReactNode }) => {
  const [drawingPoints, setDrawingPoints] = useState<ICoordinates[]>([]);
  const [formattedAddress, setFormattedAddress] = useState("");
  const [centerMarker, setCenterMarker] = useState<ICoordinates | null>(null);
  const [circleDrawing, setCircleDrawing] = useState<
    { center: ICoordinates; radius: number; type: "interest" | "avoid"; new?: boolean } | undefined
  >();
  const [polygonDrawing, setPolygonDrawing] = useState<
    { points: ICoordinates[]; type: "interest" | "avoid"; new?: boolean } | undefined
  >();
  const [radius, setRadius] = useState(500);
  const [radiusInput, setRadiusInput] = useState<string>(radius.toString());
  const [type, setType] = useState<string>("AVOID");
  const [drawingType, setDrawingType] = useState<"CIRCLE" | "POLYGON" | null>(null);
  const [shape, setShape] = useState<string>("CIRCLE");
  const [selectedOption, setSelectedOption] = useState<number>(1);
  const [drawingMarkers, setDrawingMarkers] = useState<ISandboxMarker[]>([]);
  const enableDrawingRef = useRef<boolean>(false);
  const [enableDrawingClickState, _setEnableDrawingClickState] = useState(false);
  const [mapCenter, setMapCenter] = useState<{ lat: number; lng: number }>({ lat: -23.5475, lng: -46.63611 });
  const setEnableDrawingClick = (value: boolean) => {
    enableDrawingRef.current = value;
    _setEnableDrawingClickState(value);
  };
  const enableContextMenuRef = useRef<boolean>(false);
  const [enableContextMenuClickState, _setEnableContextMenuClickState] = useState(false);
  const setEnableContextMenuClick = (value: boolean) => {
    enableContextMenuRef.current = value;
    _setEnableContextMenuClickState(value);
  };
  const [mapZoom, setMapZoom] = useState<number>(10);
  const [interestPoints, setInterestPoints] = useState<InterestPoint[]>([]);

  const fetchInterestPoints = async (search = "", page = 0, pageSize = 30) => {
    try {
      const res = await getAllInterestPoints(search, page, pageSize);
      setInterestPoints(res.content);
    } catch (error) {
      console.log("Não foi possível buscar os pontos de interesse.", error);
    }
  };

  useEffect(() => {
    fetchInterestPoints();
  }, []);

  useEffect(() => {
    console.log("mapZoom", mapZoom);
    if (mapZoom <= 5) {
      setMapZoom(5);
    }
  }, [mapZoom]);

  const formatCoordinates = (coords: ICoordinates): string => {
    const format = (value: number, isLat: boolean): string => {
      const abs = Math.abs(value).toFixed(4);
      const dir = isLat ? (value >= 0 ? "N" : "S") : value >= 0 ? "E" : "W";
      return `${abs}° ${dir}`;
    };

    return `${format(coords.lat, true)} - ${format(coords.lng, false)}`;
  };

  function sortPointsClockwise(points: ICoordinates[]): ICoordinates[] {
    const center = {
      lat: points.reduce((sum, p) => sum + p.lat, 0) / points.length,
      lng: points.reduce((sum, p) => sum + p.lng, 0) / points.length,
    };

    return points.slice().sort((a, b) => {
      const angleA = Math.atan2(a.lat - center.lat, a.lng - center.lng);
      const angleB = Math.atan2(b.lat - center.lat, b.lng - center.lng);
      return angleA - angleB;
    });
  }

  const preventDoubleClick = useRef<NodeJS.Timeout | null>(null);

  const handleMapClick = useCallback((coords: ICoordinates) => {
    if (preventDoubleClick.current) return;

    setFormattedAddress(formatCoordinates(coords));
    setCenterMarker(coords);
    setDrawingPoints((prev) => [...prev, coords]);

    preventDoubleClick.current = setTimeout(() => {
      preventDoubleClick.current = null;
    }, 300);
  }, []);

  useEffect(() => {
    if (circleDrawing) {
      setCircleDrawing((prev) =>
        prev
          ? {
              ...prev,
              radius: radius,
            }
          : undefined
      );
    }
  }, [radius]);

  useEffect(() => {
    setRadiusInput(radius.toString());
  }, [radius]);

  useEffect(() => {
    if (drawingType !== "CIRCLE") return;
    if (drawingPoints.length === 0) {
      return;
    }
    const lastPoint = drawingPoints[drawingPoints.length - 1];

    setCircleDrawing({
      center: lastPoint,
      radius,
      type: type === "AVOID" ? "avoid" : "interest",
      new: true,
    });

    if (drawingPoints.length > 1) {
      setDrawingPoints([lastPoint]);
    }
  }, [drawingPoints, drawingType, radius, type]);

  useEffect(() => {
    if (drawingType !== "POLYGON") return;

    if (drawingPoints.length >= 3) {
      const sortedPoints = sortPointsClockwise(drawingPoints);

      setPolygonDrawing({
        points: sortedPoints,
        type: type === "AVOID" ? "avoid" : "interest",
        new: true,
      });
    } else {
      setTimeout(() => {
        setPolygonDrawing(undefined);
      }, 0);
    }
  }, [drawingPoints, drawingType, type]);

  const handleChangeRadius = (newRadius: number) => {
    setRadius(newRadius);
  };

  useEffect(() => {
    if (shape === "CIRCLE" || shape === "POLYGON") {
      setDrawingType(shape);
    } else {
      setDrawingType(null);
    }
  }, [shape]);

  useEffect(() => {
    if (drawingPoints.length === 0) {
      setDrawingPoints([]);
      setCircleDrawing(undefined);
      setPolygonDrawing(undefined);
      setFormattedAddress("");
      setCenterMarker(null);
    }
  }, [shape, type]);

  return (
    <MapDrawingContext.Provider
      value={{
        handleMapClick,
        circleDrawing,
        polygonDrawing,
        radius,
        handleChangeRadius,
        drawingType,
        setDrawingType,
        drawingPoints,
        setDrawingPoints,
        type,
        setType,
        formattedAddress,
        setFormattedAddress,
        shape,
        setShape,
        selectedOption,
        setSelectedOption,
        setCircleDrawing,
        setPolygonDrawing,
        drawingMarkers,
        setDrawingMarkers,
        radiusInput,
        setRadiusInput,
        enableDrawingClick: enableDrawingRef.current,
        setEnableDrawingClick,
        enableDrawingRef,
        enableContextMenuClick: enableContextMenuRef.current,
        setEnableContextMenuClick,
        enableContextMenuRef,
        mapZoom,
        setMapZoom,
        setCenterMarker,
        centerMarker,
        sortPointsClockwise,
        mapCenter,
        setMapCenter,
        interestPoints,
      }}
    >
      {children}
    </MapDrawingContext.Provider>
  );
};
